/**
 * Editing an adjustment: an admin's free-hand balance move. It has no amount, payer or
 * split to recompute — its lines are its contents — so it gets its own small form, under
 * the same rules as making one: the lines must sum to zero.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { useBalances, useGroup } from "../../components/layout/AppShell";
import { ApiError, api, type Transaction } from "../../lib/api";
import { formatSigned, parseAmount, toAmountInput } from "../../lib/money";
import { cn } from "../../lib/utils";
import { useMembers } from "../ledger/useLedger";

export function AdjustmentForm({ groupId, transaction }: { groupId: number; transaction: Transaction }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const group = useGroup(groupId);
  const members = useMembers(groupId);
  const balances = useBalances(groupId);
  const currency = group.data?.base_currency ?? transaction.currency_code;

  const [name, setName] = useState(transaction.name);
  const [comment, setComment] = useState(transaction.comment ?? "");
  const [occurredOn, setOccurredOn] = useState(transaction.occurred_on);
  const [lines, setLines] = useState<Record<number, string>>(() =>
    Object.fromEntries(
      Object.entries(transaction.ledger ?? {}).map(([memberId, cents]) => [Number(memberId), toAmountInput(cents, currency)]),
    ),
  );
  const [error, setError] = useState<string | null>(null);

  // Everyone in the group, and anyone the adjustment already moves who has since left.
  const listed = new Map((members.data ?? []).map((member) => [member.id, member.display_name]));
  const ids = [...new Set([...listed.keys(), ...Object.keys(transaction.ledger ?? {}).map(Number)])];
  const total = Object.values(lines).reduce((sum, value) => sum + (parseAmount(value, currency) ?? 0), 0);

  const save = useMutation({
    mutationFn: () =>
      api.patch<Transaction>(
        `/groups/${groupId}/adjustments/${transaction.id}`,
        {
          name: name.trim(),
          comment: comment.trim() || null,
          occurred_on: occurredOn,
          lines: Object.entries(lines)
            .map(([memberId, value]) => ({ member_id: Number(memberId), delta_cents: parseAmount(value, currency) ?? 0 }))
            .filter((line) => line.delta_cents !== 0),
        },
        { headers: { "If-Match": String(transaction.version) } },
      ),
    onSuccess: (saved) => {
      // The drawer and the next edit read this exact entry: give them the saved copy.
      queryClient.setQueryData(["transaction", groupId, saved.id], saved);
      void queryClient.invalidateQueries({ queryKey: ["transactions", groupId] });
      void balances.refetch();
      navigate(`/g/${groupId}`);
    },
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : "Could not save the adjustment."),
  });

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
      <div className="scroll-region min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-xl px-5 py-6">
          <h1 className="display mb-1 text-sm tracking-[0.18em] text-muted">EDIT ADJUSTMENT</h1>
          <p className="mb-4 text-xs text-muted">A direct move between balances. The lines must add up to zero.</p>
          <label className="text-2xs uppercase tracking-widest text-muted">
            Name
            <Input className="mt-1" value={name} onChange={(event) => setName(event.target.value)} aria-label="Adjustment name" />
          </label>
          <label className="mt-3 block text-2xs uppercase tracking-widest text-muted">
            Date
            <Input className="mt-1" type="date" value={occurredOn} onChange={(event) => setOccurredOn(event.target.value)} />
          </label>
          <ul className="mt-4 divide-y divide-rule border-y border-rule">
            {ids.map((memberId) => {
              const label = listed.get(memberId) ?? `Former member #${memberId}`;
              return (
                <li key={memberId} className="flex items-center gap-3 py-2">
                  <span className="w-32 shrink-0 truncate text-sm">{label}</span>
                  <Input
                    inputMode="decimal"
                    className="h-9"
                    placeholder="0.00"
                    aria-label={`Adjustment for ${label}`}
                    value={lines[memberId] ?? ""}
                    onChange={(event) => setLines((current) => ({ ...current, [memberId]: event.target.value }))}
                  />
                </li>
              );
            })}
          </ul>
          <label className="mt-4 block text-2xs uppercase tracking-widest text-muted">
            Comment
            <Input className="mt-1" value={comment} onChange={(event) => setComment(event.target.value)} aria-label="Comment" />
          </label>
          {error ? (
            <p role="alert" className="mt-3 text-sm text-owing">
              {error}
            </p>
          ) : null}
        </div>
      </div>
      <div className="shrink-0 border-t border-rule bg-paper py-3">
        <div className="mx-auto flex w-full max-w-xl items-center justify-between gap-3 px-5">
          <span className={cn("num text-sm", total === 0 ? "text-muted" : "text-owing")}>Sum {formatSigned(total, currency)}</span>
          <span className="flex gap-2">
            <Button onClick={() => navigate(-1)}>Cancel</Button>
            <Button variant="primary" disabled={total !== 0 || !name.trim() || save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </span>
        </div>
      </div>
    </div>
  );
}
